import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
  Paper,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { Fair } from '../types/fair.types';
import {
  reportingService,
  StudentAward,
  ReportData,
} from '../services/reporting.service';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Public Report Component
interface PublicReportProps {
  students: StudentAward[];
  fairName: string;
}

function PublicReport({ students, fairName }: PublicReportProps) {
  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = generatePublicReportContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'science-fair-results.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    const content = generatePublicReportContent();
    if (navigator.share) {
      navigator.share({
        title: 'Science Fair Results',
        text: content,
      });
    } else {
      navigator.clipboard.writeText(content);
      alert('Results copied to clipboard!');
    }
  };

  const generatePublicReportContent = () => {
    let content = 'SCIENCE FAIR RESULTS\n';
    content += '====================\n\n';
    content += `Fair: ${fairName}\n\n`;

    const groupedByAward = students.reduce(
      (acc, student) => {
        if (!acc[student.award]) {
          acc[student.award] = [];
        }
        acc[student.award].push(student);
        return acc;
      },
      {} as Record<string, StudentAward[]>
    );

    Object.entries(groupedByAward).forEach(([award, awardStudents]) => {
      content += `${award}\n`;
      content += '-'.repeat(award.length) + '\n';

      awardStudents.forEach((student) => {
        content += `${student.placement}: ${student.name} - ${student.school}\n`;
      });
      content += '\n';
    });

    return content;
  };

  const sortedStudents = [...students].sort((a, b) => {
    // Sort by award first, then by placement
    if (a.award !== b.award) {
      return a.award.localeCompare(b.award);
    }

    const placementOrder = [
      '1st Place',
      '2nd Place',
      '3rd Place',
      'Honorable Mention',
      'Special Recognition',
      'Participation',
    ];
    const aIndex = placementOrder.indexOf(a.placement);
    const bIndex = placementOrder.indexOf(b.placement);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    return a.placement.localeCompare(b.placement);
  });

  if (students.length === 0) {
    return (
      <Card>
        <CardHeader>
          <Typography variant="h6" component="h2">
            Science Fair Results
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Results will be published here once the fair is completed
          </Typography>
        </CardHeader>
        <CardContent>
          <Box textAlign="center" py={4} color="text.secondary">
            No results available yet. Check back after the fair is completed.
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6" component="h2">
                Science Fair Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Official results for {fairName}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
              >
                Download
              </Button>
              <Button
                variant="outlined"
                onClick={handleShare}
                startIcon={<ShareIcon />}
              >
                Share
              </Button>
            </Box>
          </Box>
        </CardHeader>
      </Card>

      <Paper sx={{ p: 2 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h2">
            Science Fair Results
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }} color="text.secondary">
            {fairName}
          </Typography>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>School</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Award</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStudents.map((student) => (
              <TableRow key={student.studentId}>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {student.name}
                </TableCell>
                <TableCell>{student.school}</TableCell>
                <TableCell>{student.award}</TableCell>
                <TableCell>
                  <Chip
                    label={student.placement}
                    color={
                      student.placement.includes('1st')
                        ? 'primary'
                        : student.placement.includes('2nd')
                          ? 'secondary'
                          : student.placement.includes('3rd')
                            ? 'success'
                            : 'default'
                    }
                    variant={
                      student.placement.includes('1st') ? 'filled' : 'outlined'
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// Main PublicReportsPage Component
const PublicReportsPage: React.FC = () => {
  const [publishedReports, setPublishedReports] = useState<
    Array<{ fairId: string; data: ReportData }>
  >([]);
  const [fairs, setFairs] = useState<Record<string, Fair>>({});
  const [selectedFairId, setSelectedFairId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to published reports in real-time
  useEffect(() => {
    const unsubscribe = reportingService.subscribeToPublishedReports(
      (reports) => {
        setPublishedReports(reports);

        // Load fair details for each published report
        loadFairDetails(reports.map((r) => r.fairId));

        // Set default selected fair
        if (reports.length > 0 && !selectedFairId) {
          setSelectedFairId(reports[0].fairId);
        }

        setLoading(false);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line
  }, []);

  const loadFairDetails = async (fairIds: string[]) => {
    try {
      const fairsRef = collection(db, 'fairs');
      const fairsData: Record<string, Fair> = {};

      // Load each fair
      for (const fairId of fairIds) {
        const fairQuery = query(fairsRef, where('__name__', '==', fairId));
        const fairSnapshot = await getDocs(fairQuery);

        if (!fairSnapshot.empty) {
          const fairDoc = fairSnapshot.docs[0];
          fairsData[fairId] = {
            id: fairDoc.id,
            ...fairDoc.data(),
          } as Fair;
        }
      }

      setFairs(fairsData);
    } catch (err) {
      console.error('Error loading fair details:', err);
      setError('Failed to load fair details');
    }
  };

  const selectedReport = publishedReports.find(
    (r) => r.fairId === selectedFairId
  );
  const selectedFair = fairs[selectedFairId];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Show message if no published reports are available
  if (publishedReports.length === 0) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Box mb={4}>
          <Typography variant="h3" component="h1" gutterBottom>
            Science Fair Results
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            View official results from completed science fairs
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" gutterBottom>
              No Published Results Available
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Results will be published here once they are approved by
              administrators.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom>
          Science Fair Results
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          View official results from completed science fairs
        </Typography>
      </Box>

      {/* Fair Selection */}
      <Card sx={{ mb: 3 }}>
        <CardHeader>
          <Typography variant="h6" component="h2">
            Select Fair
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a fair to view results
          </Typography>
        </CardHeader>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Fair</InputLabel>
            <Select
              value={selectedFairId}
              onChange={(e) => setSelectedFairId(e.target.value)}
              label="Fair"
            >
              {publishedReports.map((report) => {
                const fair = fairs[report.fairId];
                return (
                  <MenuItem key={report.fairId} value={report.fairId}>
                    {fair?.name || 'Loading...'}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Results Display */}
      {selectedReport && selectedFair && (
        <PublicReport
          students={selectedReport.data.studentAwards}
          fairName={selectedFair.name}
        />
      )}
    </Box>
  );
};

export default PublicReportsPage;
